import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import {
  getMedicamentoById,
  getReco,
  createUpdateAlert,
  deleteAlert,
  getAlertas,
} from "../../services/api";

import { useFavorites } from "../../context/favoritesContext";

import "./details.css";

export default function Details() {
  const { id } = useParams();

  const [item, setItem] = useState(null);
  const [status, setStatus] = useState("idle");

  const [recs, setRecs] = useState(null);
  const [recsStatus, setRecsStatus] = useState("idle");

  const { isFavorite, markFavorite } = useFavorites();

  // =========================
  // NOTIFICACIONES / ALERTAS
  // =========================

  const [alerta, setAlerta] = useState(null);
  const [umbral, setUmbral] = useState("");
  const [alertaStatus, setAlertaStatus] = useState("idle");

  const usuario = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("usuario"));
    } catch {
      return null;
    }
  }, []);

  // =========================
  // Etiqueta por similitud
  // =========================

  function getEtiquetaPorScore(score) {
    const s = Number(score) || 0;

    if (s >= 0.9) {
      return {
        text: "Excelente",
        cls: "simil--excelente",
      };
    }

    if (s >= 0.8) {
      return {
        text: "Alta",
        cls: "simil--alta",
      };
    }

    if (s >= 0.65) {
      return {
        text: "Media",
        cls: "simil--media",
      };
    }

    if (s >= 0.5) {
      return {
        text: "Baja",
        cls: "simil--baja",
      };
    }

    return {
      text: "Lejana",
      cls: "simil--lejana",
    };
  }

  // =========================
  // Fetch medicamento
  // =========================

  useEffect(() => {
    let mounted = true;

    async function fetchMedicamento() {
      setStatus("loading");

      try {
        const data = await getMedicamentoById(id);

        if (mounted) {
          setItem(data);
          setStatus("ok");
        }
      } catch (e) {
        console.error("Error cargando medicamento:", e);

        if (mounted) {
          setStatus("error");
        }
      }
    }

    fetchMedicamento();

    return () => {
      mounted = false;
    };
  }, [id]);

  // =========================
  // Cargar alerta existente
  // =========================

  useEffect(() => {
    if (!usuario?._id || !id) return;

    let mounted = true;

    async function fetchAlerta() {
      setAlertaStatus("loading");

      try {
        const resp = await getAlertas();

        if (!mounted) return;

        // La API devuelve:
        // {
        //   count: N,
        //   data: [...]
        // }

        const alertas = Array.isArray(resp)
          ? resp
          : Array.isArray(resp?.data)
          ? resp.data
          : Array.isArray(resp?.alertas)
          ? resp.alertas
          : [];

        // Buscar la alerta correspondiente al medicamento actual
        const alertaExistente = alertas.find((a) => {
          const medicamentoId =
            a?.medicamento_id?._id ??
            a?.medicamento_id;

          return (
            String(medicamentoId) === String(id) &&
            a?.activa !== false
          );
        });

        if (alertaExistente) {
          setAlerta(alertaExistente);

          setUmbral(
            String(
              alertaExistente.umbralPrecio ?? ""
            )
          );
        } else {
          setAlerta(null);
          setUmbral("");
        }

        setAlertaStatus("ok");
      } catch (e) {
        console.error(
          "Error cargando alerta:",
          e
        );

        if (!mounted) return;

        setAlerta(null);
        setUmbral("");
        setAlertaStatus("error");
      }
    }

    fetchAlerta();

    return () => {
      mounted = false;
    };
  }, [usuario?._id, id]);

  // =========================
  // Fetch recomendaciones
  // =========================

  useEffect(() => {
    if (!item?._id) return;

    let mounted = true;

    async function fetchRecomendaciones() {
      setRecsStatus("loading");

      try {
        const data = await getReco(
          item._id,
          8
        );

        if (mounted) {
          setRecs(data);
          setRecsStatus("ok");
        }
      } catch (e) {
        console.error(
          "Error cargando recomendaciones:",
          e
        );

        if (mounted) {
          setRecsStatus("error");
        }
      }
    }

    fetchRecomendaciones();

    return () => {
      mounted = false;
    };
  }, [item?._id]);

  // =========================
  // Formatos y datos derivados
  // =========================

  const fmtMoney = useMemo(() => {
    const nf = new Intl.NumberFormat(
      "es-MX",
      {
        style: "currency",
        currency: "MXN",
        maximumFractionDigits: 2,
      }
    );

    return (v) =>
      v == null
        ? "—"
        : nf.format(Number(v));
  }, []);

  const m = useMemo(() => {
    if (!item) return null;

    return {
      ...item,
      precioActual:
        item?.precioActual ??
        item?.precio,
      precio:
        item?.precio ??
        item?.precioActual,
      enlaceImagen:
        item?.enlaceImagen ??
        item?.imagen,
    };
  }, [item]);

  const precioActualVal = useMemo(
    () =>
      m?.precioActual ??
      m?.precio,
    [m]
  );

  const precioActualFmt = useMemo(
    () =>
      fmtMoney(
        precioActualVal
      ),
    [
      precioActualVal,
      fmtMoney,
    ]
  );

  const precioMinimoFmt = useMemo(
    () =>
      fmtMoney(
        m?.precioMinimo
      ),
    [m, fmtMoney]
  );

  const activosArr = useMemo(() => {
    const a =
      m?.sustanciaActiva;

    if (!a) return [];

    return Array.isArray(a)
      ? a.filter(Boolean)
      : [a].filter(Boolean);
  }, [m]);

  const dosificacionTxt = useMemo(
    () => {
      const d =
        m?.dosificacionLista;

      if (
        !Array.isArray(d) ||
        d.length === 0
      ) {
        return "—";
      }

      return d.join(" / ");
    },
    [m]
  );

  const fechaTxt = useMemo(() => {
    const f =
      m?.fechaExtraccion;

    if (!f) return "—";

    const dt =
      new Date(f);

    return Number.isNaN(
      dt.getTime()
    )
      ? "—"
      : dt.toLocaleString(
          "es-MX"
        );
  }, [m]);

  const fav =
    isFavorite(m?._id);

  // =========================
  // Estado de alerta
  // =========================

  const alertaExiste = useMemo(
    () => {
      return !!(
        alerta &&
        (
          alerta._id ||
          alerta.medicamento_id
        )
      );
    },
    [alerta]
  );

  const umbralNumero = useMemo(
    () =>
      Number(umbral),
    [umbral]
  );

  const umbralValido = useMemo(
    () => {
      return (
        Number.isFinite(
          umbralNumero
        ) &&
        umbralNumero > 0
      );
    },
    [umbralNumero]
  );

  const umbralGuardado = useMemo(
    () => {
      return Number(
        alerta?.umbralPrecio ??
          0
      );
    },
    [alerta]
  );

  const umbralCambio = useMemo(
    () => {
      if (!umbralValido)
        return false;

      if (!alertaExiste)
        return true;

      return (
        umbralNumero !==
        umbralGuardado
      );
    },
    [
      umbralValido,
      alertaExiste,
      umbralNumero,
      umbralGuardado,
    ]
  );

  const puedeGuardar = useMemo(
    () => {
      return (
        !!usuario?._id &&
        alertaStatus !==
          "saving" &&
        umbralValido &&
        umbralCambio
      );
    },
    [
      usuario?._id,
      alertaStatus,
      umbralValido,
      umbralCambio,
    ]
  );

  const puedeDesactivar = useMemo(
    () => {
      return (
        !!usuario?._id &&
        alertaStatus !==
          "saving" &&
        alertaExiste
      );
    },
    [
      usuario?._id,
      alertaStatus,
      alertaExiste,
    ]
  );

  // =========================
  // Acciones alerta
  // =========================

  async function onGuardarAlerta() {
    if (!usuario?._id) {
      toast.error(
        "Primero debe iniciar sesión."
      );

      setAlertaStatus(
        "error"
      );

      return;
    }

    if (!item?._id)
      return;

    const n =
      Number(umbral);

    if (
      !Number.isFinite(n) ||
      n <= 0
    ) {
      toast.error(
        "Ingrese un número válido."
      );

      setAlertaStatus(
        "error"
      );

      return;
    }

    setAlertaStatus(
      "saving"
    );

    try {
      const doc =
        await createUpdateAlert({
          usuario_id:
            usuario._id,
          medicamento_id:
            item._id,
          umbralPrecio: n,
          activa: true,
        });

      const normalized =
        doc?.data ??
        doc ??
        null;

      setAlerta(
        normalized
      );

      setUmbral(
        String(n)
      );

      setAlertaStatus(
        "ok"
      );

      toast.success(
        "Notificación guardada"
      );
    } catch (e) {
      console.error(
        "Error guardando alerta:",
        e
      );

      setAlertaStatus(
        "error"
      );

      toast.error(
        e?.response
          ?.data?.message ||
          "No se pudo guardar la notificación."
      );
    }
  }

  async function onDesactivarAlerta() {
    if (!usuario?._id) {
      toast.error(
        "Primero debe iniciar sesión."
      );

      setAlertaStatus(
        "error"
      );

      return;
    }

    if (!item?._id)
      return;

    if (!alertaExiste)
      return;

    setAlertaStatus(
      "saving"
    );

    try {
      await deleteAlert({
        usuario_id:
          usuario._id,
        medicamento_id:
          item._id,
      });

      setAlerta(null);

      setUmbral("");

      setAlertaStatus(
        "ok"
      );

      toast.success(
        "Notificación desactivada"
      );
    } catch (e) {
      console.error(
        "Error desactivando alerta:",
        e
      );

      setAlertaStatus(
        "error"
      );

      toast.error(
        e?.response
          ?.data?.message ||
          "No se pudo desactivar la notificación."
      );
    }
  }

  // =========================
  // Normalizar recomendaciones
  // =========================

  const recList = useMemo(
    () => {
      const arr =
        recs?.recomendaciones ||
        [];

      return arr
        .slice(0, 8)
        .map((r) => {
          const scoreRaw =
            r?.score ??
            r?.puntaje ??
            r?.similitud ??
            0;

          const score =
            Number(
              scoreRaw
            ) || 0;

          const tag =
            getEtiquetaPorScore(
              score
            );

          return {
            ...r,
            _id: r?._id,
            nombre:
              r?.nombre ??
              "—",
            farmacia:
              r?.farmacia ??
              "—",
            enlace:
              r?.enlace ??
              "",
            enlaceImagen:
              r?.enlaceImagen ??
              r?.imagen ??
              "",
            precioActual:
              r?.precioActual ??
              r?.precio ??
              null,
            precioPorTableta:
              r?.precioPorTableta ??
              null,
            score,
            tagText:
              tag.text,
            tagClass:
              tag.cls,
          };
        });
    },
    [recs]
  );

  if (
    status ===
    "loading"
  ) {
    return (
      <div className="details container">
        <div className="details__skeleton">
          <div className="sk-img" />
          <div className="sk-text" />
          <div className="sk-text sm" />
          <div className="sk-text sm" />
        </div>
      </div>
    );
  }

  if (
    status ===
    "error"
  ) {
    return (
      <div className="details container">
        <div className="details__error">
          <h2>
            Ups… no se pudo cargar el
            medicamento.
          </h2>

          <p>
            Intenta de nuevo más tarde.
          </p>

          <Link
            className="btn-link"
            to="/buscar"
          >
            Volver a buscar
          </Link>
        </div>
      </div>
    );
  }

  if (!m)
    return null;

  return (
    <div className="details container">
      <nav className="details__breadcrumbs">
        <Link to="/">
          Inicio
        </Link>

        <span>
          {" "}
          /{" "}
        </span>

        <Link to="/buscar">
          Buscar
        </Link>

        <span>
          {" "}
          /{" "}
        </span>

        <span className="current">
          {m.nombre}
        </span>
      </nav>

      <div className="details__card">
        <div className="details__media">
          <img
            className="details__img"
            src={
              m.enlaceImagen ||
              "https://via.placeholder.com/480x360?text=Sin+imagen"
            }
            alt={m.nombre}
            onError={(e) => {
              e.currentTarget.src =
                "https://via.placeholder.com/480x360?text=Sin+imagen";
            }}
          />
        </div>

        <div className="details__info">
          <h1 className="details__title">
            {m.nombre}
          </h1>

          <div className="pill-row">
            {activosArr.map(
              (s) => (
                <span
                  key={s}
                  className="pill"
                >
                  {s}
                </span>
              )
            )}
          </div>

          <div className="details__meta">
            <div>
              <strong>
                Farmacia:
              </strong>{" "}

              <span className="price">
                {m.farmacia ||
                  "—"}
              </span>
            </div>

            <div>
              <strong>
                Precio actual:
              </strong>{" "}

              <span className="price">
                {
                  precioActualFmt
                }
              </span>
            </div>

            <div>
              <strong>
                Precio mínimo:
              </strong>{" "}

              {
                precioMinimoFmt
              }
            </div>

            <div>
              <strong>
                Cantidad:
              </strong>{" "}

              {m.cantidadTabletas ??
                "—"}
            </div>

            <div>
              <strong>
                Precio por unidad:
              </strong>{" "}

              {fmtMoney(
                m.precioPorTableta
              )}
            </div>

            <div>
              <strong>
                Dosificación:
              </strong>{" "}

              {
                dosificacionTxt
              }
            </div>

            {m.disponible ===
              false && (
              <div className="tag tag--warn">
                No disponible
              </div>
            )}
          </div>

          <div className="details__actions">
            {m.enlace && (
              <a
                href={
                  m.enlace
                }
                target="_blank"
                rel="noreferrer"
                className="btn-primary"
              >
                Ver en la tienda
              </a>
            )}

            <button
              type="button"
              className={`btn-outline ${
                fav
                  ? "is-fav"
                  : ""
              }`}
              onClick={() =>
                markFavorite(m)
              }
              title={
                fav
                  ? "Quitar de favoritos"
                  : "Añadir a favoritos"
              }
              aria-label={
                fav
                  ? "Quitar de favoritos"
                  : "Añadir a favoritos"
              }
            >
              {fav
                ? "★ En favoritos"
                : "☆ Añadir a favoritos"}
            </button>

            <button
              type="button"
              className="btn-outline"
              onClick={() => {
                navigator.clipboard.writeText(
                  window.location
                    .href
                );

                toast.success(
                  "Enlace copiado"
                );
              }}
            >
              Compartir
            </button>
          </div>

          <div className="details__extra">
            <small>
              Última extracción:{" "}
              {fechaTxt}
            </small>
          </div>
        </div>
      </div>

      <section className="details__alerts">
        <div className="alerts-card">
          <div className="alerts-card__header">
            <div>
              <h3 className="alerts-card__title">
                Notificaciones de precio
              </h3>

              <p className="alerts-card__desc">
                Ingresa el precio máximo que estarías dispuesto a pagar.
                Se notificará cuando este medicamento baje a ese precio o menos.
              </p>
            </div>

            <div className="alerts-card__account">
              {usuario?._id ? (
                <span className="muted tiny">
                  Cuenta:{" "}
                  {usuario?.correo ||
                    "—"}
                </span>
              ) : (
                <span className="muted tiny">
                  Inicie sesión para activar
                </span>
              )}
            </div>
          </div>

          <div className="alerts-card__controls">
            <label className="alerts-card__label">
              <span className="muted">
                Avisarme cuando el precio sea menor a:
              </span>

              <input
                className="alerts-card__input"
                type="number"
                min="1"
                step="1"
                value={umbral}
                onChange={(e) =>
                  setUmbral(
                    e.target.value
                  )
                }
                disabled={
                  alertaStatus ===
                  "saving"
                }
                placeholder="Ej. 80"
              />
            </label>

            <div className="alerts-card__buttons">
              <button
                type="button"
                className={`btn-state btn-state--success ${
                  !puedeGuardar
                    ? "is-disabled"
                    : ""
                }`}
                onClick={
                  onGuardarAlerta
                }
                disabled={
                  !puedeGuardar
                }
                title={
                  alertaExiste
                    ? "Guardar umbral"
                    : "Activar alerta"
                }
              >
                {alertaExiste
                  ? "Guardar umbral"
                  : "Activar alerta"}
              </button>

              <button
                type="button"
                className={`btn-state btn-state--danger ${
                  !puedeDesactivar
                    ? "is-disabled"
                    : ""
                }`}
                onClick={
                  onDesactivarAlerta
                }
                disabled={
                  !puedeDesactivar
                }
              >
                Desactivar alerta
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="details__recs">
        <h2>
          Medicamentos similares
        </h2>

        <p className="details__recs__desc">
          Mostramos medicamentos similares priorizando el principio activo, seguido de la dosificación y la presentación, con el objetivo de ofrecer alternativas lo más cercanas posible.
        </p>

        {recsStatus ===
          "loading" && (
          <p className="muted">
            Cargando recomendaciones…
          </p>
        )}

        {recsStatus ===
          "error" && (
          <p className="muted">
            No se pudieron cargar las recomendaciones.
          </p>
        )}

        {recsStatus ===
          "ok" &&
          recList.length ===
            0 && (
            <p className="muted">
              No se encontraron alternativas similares.
            </p>
          )}

        {recsStatus ===
          "ok" &&
          recList.length >
            0 && (
            <div className="grid-cards grid-cards--compact">
              {recList.map(
                (r) => {
                  const recFav =
                    isFavorite(
                      r?._id
                    );

                  return (
                    <article
                      key={r._id}
                      className="med-card rec-card rec-card--compact"
                    >
                      <button
                        type="button"
                        className={`med-card__fav ${
                          recFav
                            ? "is-fav"
                            : ""
                        }`}
                        onClick={() =>
                          markFavorite(
                            r
                          )
                        }
                        title={
                          recFav
                            ? "Quitar de favoritos"
                            : "Añadir a favoritos"
                        }
                        aria-label={
                          recFav
                            ? "Quitar de favoritos"
                            : "Añadir a favoritos"
                        }
                      >
                        {recFav
                          ? "★"
                          : "☆"}
                      </button>

                      <div className="rec-card__image-wrap">
                        {r.enlaceImagen ? (
                          <img
                            className="rec-card__image"
                            src={
                              r.enlaceImagen
                            }
                            alt={
                              r.nombre
                            }
                            loading="lazy"
                            onError={(
                              e
                            ) => {
                              e.currentTarget.src =
                                "https://via.placeholder.com/240x180?text=Sin+imagen";
                            }}
                          />
                        ) : (
                          <div className="rec-card__placeholder">
                            Sin imagen
                          </div>
                        )}
                      </div>

                      <div className="rec-card__body">
                        <div className="rec-row rec-row--compact">
                          <span
                            className={`simil ${r.tagClass}`}
                          >
                            {
                              r.tagText
                            }
                          </span>

                          <span className="score">
                            {(
                              r.score *
                              100
                            ).toFixed(
                              0
                            )}
                            %
                          </span>
                        </div>

                        <h4 className="rec-card__title">
                          {
                            r.nombre
                          }
                        </h4>

                        <p className="rec-card__farmacia">
                          {
                            r.farmacia
                          }
                        </p>

                        <p className="rec-card__price">
                          {fmtMoney(
                            r.precioActual
                          )}

                          {r.precioPorTableta !=
                          null ? (
                            <span className="muted tiny">
                              {" "}
                              (
                              {fmtMoney(
                                r.precioPorTableta
                              )}{" "}
                              / unidad)
                            </span>
                          ) : null}
                        </p>

                        <div className="rec-card__links">
                          <Link
                            className="rec-card__link"
                            to={`/detalles/${r._id}`}
                          >
                            Ver detalles
                          </Link>

                          {r.enlace && (
                            <a
                              href={
                                r.enlace
                              }
                              target="_blank"
                              rel="noreferrer"
                              className="rec-card__link alt"
                            >
                              Ver en tienda
                            </a>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                }
              )}
            </div>
          )}
      </section>

      <div className="details__back">
        <Link
          to="/buscar"
          className="btn-link"
        >
          ← Volver
        </Link>
      </div>
    </div>
  );
}